import { addMonths, format, subMonths } from 'date-fns';
import reverse from 'lodash/reverse';

/**
 * Remove null key/value pairs from an object.
 * @param {Object} obj
 */
export const filterObjectNonNull = (obj) =>
    Object.fromEntries(
        Object.entries(obj).filter(([_, v]) => !Object.is(v, null)) // eslint-disable-line no-unused-vars
    );

/**
 * Convert epoch time to date string.
 * @param {Number} epoch Epoch time
 * @param {String} pattern Date format pattern
 * @returns {String} Formatted date
 */
export const convertEpochToDateString = (epoch, pattern) =>
    format(new Date(epoch), pattern);

/**
 * Convert timeslicer date object to epoch time.
 * @param {Object} date Timeslicer date object
 * @returns {Number} Epoch time
 */
export const convertDateToEpoch = (date) => {
    const { month, year } = date;
    const dateTimeString = `${year}-${month}-01 00:00:00`;
    return new Date(dateTimeString).getTime();
};

/**
 * Generate month list for timeslicer.
 * @param {Number} endDateEpoch end date in epoch time
 * @returns Generated 12 months from end date into past.
 */
export const generateMonthListByEndDate = (endDateEpoch) => {
    const MONTH_COUNT = 12;

    const months = Array.from(Array(MONTH_COUNT).keys()).map((i) => {
        const dateString = format(
            subMonths(new Date(endDateEpoch), i),
            'MM MMM yyyy'
        ).split(' ');

        const month = dateString[0];
        const year = dateString[2];

        return {
            year,
            month,
            monthText: dateString[1],
            monthNum: parseInt(dateString[0], 10),
            epoch: convertDateToEpoch({ month, year })
        };
    });

    return reverse(months);
};

/**
 * Get next month to include full last month.
 * @param {Object} dateObj Date object with first day of month
 * @returns Date object with first day of +1 month
 */
export const increaseEndDateByOneMonth = (dateObj) => {
    if (!dateObj) {
        return {};
    }

    const { month, year } = dateObj;
    const dateString = format(
        addMonths(new Date(year, month - 1, 1, 0, 0, 0), 1),
        'MM MMM yyyy'
    ).split(' ');

    const newMonth = dateString[0];
    const newYear = dateString[2];

    return {
        year: newYear,
        month: newMonth,
        monthText: dateString[1],
        monthNum: parseInt(dateString[0], 10),
        epoch: convertDateToEpoch({ month: newMonth, year: newYear })
    };
};

/**
 * Get previous month.
 * @param {Object} dateObj Date object with first day of month
 * @returns Date object with first day of -1 month
 */
export const decreaseEndDateByOneMonth = (dateObj) => {
    if (!dateObj) {
        return {};
    }

    const { month, year } = dateObj;
    const dateString = format(
        subMonths(new Date(year, month - 1, 1, 0, 0, 0), 1),
        'MM MMM yyyy'
    ).split(' ');

    const newMonth = dateString[0];
    const newYear = dateString[2];

    return {
        year: newYear,
        month: newMonth,
        monthText: dateString[1],
        monthNum: parseInt(dateString[0], 10),
        epoch: convertDateToEpoch({ month: newMonth, year: newYear })
    };
};

/**
 * Convert a number that is more than 1000 or less than -1000 to {n}"k"
 * @param {Number} num number to convert
 * @returns {String} Converted string with k
 */
export const convertThousandToK = (num) =>
    num > 1000 || num < -1000
        ? `${Math.ceil(Math.floor(num / 10) / 10) / 10}k`
        : num;

/**
 * Check if value is a number, if so, format to 2 decimal places, if not just return the value
 * @param {String|Number} value value to format
 * @returns {String|Number} Number converted to 2 decimal places or original value
 */
export const format2DP = (value) => {
    if (typeof value === 'number') {
        if (value === 0 || value === 100) {
            return value;
        } else {
            return value.toFixed(2);
        }
    }
    return value;
};

export const currencyFormatter = (amount) =>
    amount && amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
